import { useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { Popover } from "@/components/common/Popover";
import { formatBytes } from "@/lib/format-bytes";
import { placeholderKey, type ChatFile, type ImagePlaceholderTarget } from "@/lib/chat-files";
import { imageBlockSrc, type ImageContentBlock } from "@/lib/chat";
import { useToastStore } from "@/stores/useToastStore";
import { ImageLightbox } from "./ImageLightbox";
import { useChatImagePlaceholders } from "./useImagePlaceholders";

/** ポップアップの幅 (px)。ファイル名と容量を 1 行に収めつつ、履歴を覆いすぎない値。 */
const POPOVER_WIDTH = 320;

/** 一覧の最大高さ (px)。これを超えるとポップアップ内でスクロールする。 */
const LIST_MAX_HEIGHT = 360;

/** サムネイル・アイコン枠の一辺 (px)。 */
const THUMBNAIL_SIZE = 32;

interface ChatFilesPopoverProps {
	chatId: string;
	files: readonly ChatFile[];
	/** 開閉ボタン。ここへのクリックは「外側クリック」として扱わない (ボタン側がトグルする)。 */
	anchorRef: RefObject<HTMLElement | null>;
	onClose: () => void;
}

/**
 * チャットに読み込まれたファイルの一覧。配置は親 (ChatHeader) が決める。
 * 外側クリックか Escape で閉じる。画像はクリックで原寸表示し、モデル入力から外す (プレースホルダー化) 操作と
 * その取り消しもここから行う。履歴の表示は変わらず、以降のモデル呼び出しだけが影響を受ける。
 */
export function ChatFilesPopover({ chatId, files, anchorRef, onClose }: ChatFilesPopoverProps) {
	const [openImage, setOpenImage] = useState<ImageContentBlock | null>(null);
	const [busy, setBusy] = useState(false);
	// 置き換え状態 (replacedKeys) を取得できるまでは操作を出さない (取得失敗時も同様で、下に理由を示す)。
	const { replacedKeys, loadError, replace, restore } = useChatImagePlaceholders(chatId);

	const run = async (action: () => Promise<void>, fallbackMessage: string) => {
		setBusy(true);
		try {
			await action();
		} catch (err: unknown) {
			useToastStore.getState().show({
				type: "error",
				message: err instanceof Error ? err.message : fallbackMessage,
			});
			throw err;
		} finally {
			setBusy(false);
		}
	};
	const replaceOne = (target: ImagePlaceholderTarget) =>
		run(() => replace([target]), "画像の置き換えに失敗しました。");
	const restoreOne = (target: ImagePlaceholderTarget) =>
		run(() => restore([target]), "置き換えの取り消しに失敗しました。");

	return (
		// ライトボックスを開いている間はその操作 (背景クリック・Escape) をこの一覧の閉じる操作と
		// 混同しないよう、外側クリックと Escape では閉じない。
		<Popover
			anchorRef={anchorRef}
			onClose={onClose}
			dismissible={openImage === null}
			width={POPOVER_WIDTH}
			padding={4}
			testId="chat-files-popover"
		>
			<div
				style={{
					padding: "6px 8px 4px",
					fontSize: "var(--text-section)",
					fontWeight: 600,
					letterSpacing: "var(--tracking-label)",
					textTransform: "uppercase",
					color: "var(--fg-3)",
				}}
			>
				読み込まれたファイル ({files.length})
			</div>
			{loadError !== null && (
				<div
					data-testid="chat-files-placeholders-error"
					style={{
						padding: "4px 8px 6px",
						fontSize: "var(--text-caption)",
						color: "var(--danger)",
					}}
				>
					{loadError}
				</div>
			)}
			{files.length === 0 ? (
				<div
					data-testid="chat-files-empty"
					style={{
						padding: "8px 8px 10px",
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
					}}
				>
					このチャットにはまだファイルが読み込まれていません
				</div>
			) : (
				<ul
					style={{
						listStyle: "none",
						margin: 0,
						padding: 0,
						maxHeight: LIST_MAX_HEIGHT,
						overflowY: "auto",
					}}
				>
					{files.map((file) => {
						const replaced =
							file.kind === "image" && replacedKeys !== null
								? replacedKeys.has(placeholderKey(file.target))
								: null;
						return (
							<ChatFileRow
								key={file.key}
								file={file}
								replaced={replaced === true}
								onOpenImage={file.kind === "image" ? () => setOpenImage(file.image) : undefined}
								onToggleReplaced={
									file.kind === "image" && replaced !== null
										? () => (replaced ? restoreOne(file.target) : replaceOne(file.target))
										: undefined
								}
								actionDisabled={busy}
							/>
						);
					})}
				</ul>
			)}
			{openImage &&
				createPortal(
					<ImageLightbox image={openImage} onClose={() => setOpenImage(null)} />,
					document.body,
				)}
		</Popover>
	);
}

interface ChatFileRowProps {
	file: ChatFile;
	/** モデル入力から外されている画像か。 */
	replaced: boolean;
	onOpenImage?: () => void;
	/** 置き換え (未置換の行) または取り消し (置換済みの行)。状態が未取得の行と文書では undefined。 */
	onToggleReplaced?: () => Promise<void>;
	actionDisabled: boolean;
}

function ChatFileRow({
	file,
	replaced,
	onOpenImage,
	onToggleReplaced,
	actionDisabled,
}: ChatFileRowProps) {
	// 画像の寸法はサムネイルの読み込み完了後に確定する。
	const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
	const [hovered, setHovered] = useState(false);
	const meta = [
		replaced ? "プレースホルダーに置換済み" : null,
		file.kind === "uploaded" ? file.path : null,
		dimensions === null ? null : `${dimensions.width}×${dimensions.height} px`,
		formatBytes(file.byteSize),
	]
		.filter((part) => part !== null)
		.join(" · ");
	return (
		<li style={{ display: "flex", alignItems: "center", gap: 2 }}>
			<button
				type="button"
				data-testid="chat-file-row"
				disabled={onOpenImage === undefined}
				onClick={onOpenImage}
				onPointerEnter={() => setHovered(true)}
				onPointerLeave={() => setHovered(false)}
				title={
					onOpenImage
						? `${file.name}\nクリックで拡大`
						: file.kind === "uploaded"
							? file.path
							: file.name
				}
				data-tooltip-if-truncated={onOpenImage ? undefined : true}
				style={{
					display: "flex",
					alignItems: "center",
					gap: 10,
					flex: "1 1 auto",
					minWidth: 0,
					padding: "6px 8px",
					border: "none",
					borderRadius: "var(--radius-2)",
					background: hovered && onOpenImage ? "var(--menu-hover-bg)" : "transparent",
					color: "inherit",
					font: "inherit",
					textAlign: "left",
					cursor: onOpenImage ? "pointer" : "default",
				}}
			>
				{file.kind === "image" ? (
					<img
						data-testid="chat-file-thumbnail"
						loading="lazy"
						src={imageBlockSrc(file.image)}
						onLoad={(e) =>
							setDimensions({
								width: e.currentTarget.naturalWidth,
								height: e.currentTarget.naturalHeight,
							})
						}
						style={{
							width: THUMBNAIL_SIZE,
							height: THUMBNAIL_SIZE,
							flexShrink: 0,
							objectFit: "cover",
							borderRadius: "var(--radius-2)",
							boxShadow: "var(--shadow-ring)",
							background: "var(--surface-2)",
							opacity: replaced ? 0.4 : 1,
						}}
					/>
				) : (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: THUMBNAIL_SIZE,
							height: THUMBNAIL_SIZE,
							flexShrink: 0,
							borderRadius: "var(--radius-2)",
							background: "var(--surface-2)",
							boxShadow: "var(--shadow-ring)",
						}}
					>
						<Icon name="file" size={16} color="var(--fg-3)" />
					</span>
				)}
				<span style={{ display: "grid", minWidth: 0, gap: 2 }}>
					<span
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							fontSize: "var(--text-ui)",
							color: hovered && onOpenImage ? "var(--fg-0)" : "var(--fg-1)",
						}}
					>
						{file.name}
					</span>
					<span
						data-testid="chat-file-meta"
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							fontSize: "var(--text-caption)",
							color: "var(--fg-3)",
						}}
					>
						{meta}
					</span>
				</span>
			</button>
			{onToggleReplaced && (
				<IconButton
					icon={replaced ? "undo" : "trash"}
					data-tooltip={
						replaced
							? "置き換えを取り消し、画像を再びモデル入力に載せる"
							: "以降のモデル入力でこの画像をプレースホルダーに置き換える"
					}
					disabled={actionDisabled}
					onClick={onToggleReplaced}
					data-testid={replaced ? "chat-file-restore" : "chat-file-replace"}
					style={{ flexShrink: 0, marginRight: 4 }}
				/>
			)}
		</li>
	);
}
